import '../PricingCreateForm.css';

import { EditVatForm } from './EditVatForm';
import React from 'react';
import { Spinner } from '../../../../../components/Spinner/Spinner';
import { Stack } from '../../../../../components/Stack/Stack';
import { graphql } from '../../../../../generated';
import { isEmpty, sortBy } from 'lodash';
import { useQueryHandler } from '../../../../../model/graphql';

interface Props {
  productId: string;
  onClose: () => void;
  onTaxUpdate: () => void;
}

export type TaxRate = {
  amount: number;
  name: string;
};

export type Tax = {
  amount: number;
  linked: boolean;
};

const PRODUCT_TAXES_WITH_DEFAULT_RATES = graphql(`
  query ProductTaxesWithDefaultRates($id: ID!) {
    product(id: $id) {
      id
      taxes {
        amount
        countryCode
        linked
      }
    }
    taxRates {
      nodes {
        id
        amount
        country {
          countryCode
          name
        }
      }
    }
  }
`);

const DEFAULT_TAX_VALUE = 0.19;

export function EditVat({ productId, onClose, onTaxUpdate }: Props): JSX.Element | null {
  const { data, loading } = useQueryHandler(PRODUCT_TAXES_WITH_DEFAULT_RATES, {
    variables: {
      id: productId,
    },
    defaultOptions: {
      fetchPolicy: 'no-cache',
    },
  });

  if (loading) {
    return (
      <Stack>
        <Spinner />
      </Stack>
    );
  }

  const defaultTaxRates = sortBy(data?.taxRates.nodes, 'country.name').reduce(
    (acc, current) => {
      return {
        ...acc,
        [current?.country?.countryCode as string]: {
          amount: current?.amount || 0,
          name: current?.country?.name || '',
        },
      };
    },
    {} as Record<string, TaxRate>
  );

  const worldTax = data?.product?.taxes.find((tax) => tax.countryCode === null);

  // for new product this is an empty array
  const productTaxRates = data?.product?.taxes.reduce(
    (acc, current) => {
      return {
        ...acc,
        [current.countryCode as string]: {
          amount: current?.amount,
          linked: current?.linked || false,
        },
      };
    },
    {} as Record<string, Tax>
  );

  const { de, ...rest } = Object.fromEntries(
    Object.entries(defaultTaxRates).map(([key, value]) => [
      key,
      {
        amount: productTaxRates?.[key]?.amount ?? value.amount ?? DEFAULT_TAX_VALUE,
        name: value.name,
        linked: productTaxRates?.[key]?.linked || false,
        originalTax: value.amount,
      },
    ])
  );

  return (
    <EditVatForm
      key={JSON.stringify(productTaxRates)}
      productId={productId}
      countries={{ de, world: { name: 'World', amount: worldTax?.amount || 0 }, ...rest }}
      onClose={onClose}
      newProduct={isEmpty(productTaxRates)}
      onAfterSubmit={onTaxUpdate}
    />
  );
}
